import type { IgContentType, PostMedia } from '../../../types/publishing';
import { BrandCircle, PostMediaView } from './PostMediaView';
import { LinkifiedText } from './LinkifiedText';
import { InstagramCarousel } from './InstagramCarousel';
import { instagramCarouselApplies } from '../../../lib/publishing/instagramCarousel';

// Publishing preview — Instagram. Feed (square) / Reel (9:16) / Story (9:16). IG
// infers feed-vs-Reel from media; the toggle drives the frame shape for preview.

export function InstagramPreview({
  handle,
  content,
  media,
  contentType,
  firstComment,
}: {
  handle: string;
  content: string;
  media: PostMedia[];
  contentType: IgContentType;
  firstComment: string | null;
}) {
  if (contentType === 'story') {
    return (
      <div className="mx-auto max-w-[260px] w-full">
        <div className="relative rounded-xl overflow-hidden border border-slate-200 bg-black">
          <PostMediaView media={media} aspectClass="aspect-[9/16]" />
          <div className="absolute top-0 inset-x-0 p-3 flex items-center gap-2 bg-gradient-to-b from-black/50 to-transparent">
            <BrandCircle name={handle} />
            <span className="text-xs font-semibold text-white truncate">{handle}</span>
          </div>
        </div>
        <p className="mt-1 text-center text-xs text-slate-500">Instagram Story</p>
      </div>
    );
  }
  const isReel = contentType === 'reel';
  return (
    <article className="mx-auto max-w-[380px] w-full rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
      <header className="flex items-center gap-3 px-3 py-2.5">
        <BrandCircle name={handle} />
        <p className="flex-1 text-sm font-semibold text-slate-900 truncate">{handle}</p>
        <span className="text-slate-400" aria-hidden>⋯</span>
      </header>
      {/* A Reel is a fixed 9:16 surface (Meta crops to fill), so it keeps the
          fixed frame + crop; the true feed placement sizes to the image's own
          ratio (natural — no fixed box, no letterbox bars). The feed aspectClass
          is only the empty-state placeholder shape. */}
      {instagramCarouselApplies(media, isReel ? 'reel' : 'feed') ? (
        <InstagramCarousel media={media} />
      ) : (
        <PostMediaView
          media={media}
          aspectClass={isReel ? 'aspect-[9/16]' : 'aspect-square'}
          fit={isReel ? 'cover' : 'natural'}
        />
      )}
      <div className="px-3 py-2 flex items-center gap-4 text-slate-700 text-lg" aria-hidden>
        <span>♡</span>
        <span>💬</span>
        <span>➤</span>
      </div>
      {content.trim() !== '' && (
        <p className="px-3 pb-2 text-sm text-slate-800">
          <span className="font-semibold mr-1">{handle}</span>
          <span className="whitespace-pre-wrap">
            <LinkifiedText
              text={content}
              linkTitle="Instagram does not make caption links clickable. This link is here so you can check where it goes."
            />
          </span>
        </p>
      )}
      {firstComment && firstComment.trim() !== '' && (
        <p className="px-3 pb-3 text-xs text-slate-600">
          <span className="font-semibold mr-1">{handle}</span>
          {firstComment}
        </p>
      )}
      {isReel && <p className="px-3 pb-2 text-[11px] text-slate-400">Reel</p>}
    </article>
  );
}
